import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { get } from "@insite/client-framework/Services/ApiService";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { BiomedicalDivisionEmailRequest } from "@preferredMedical/Models/CustomForms/BiomedicalDivision/BiomedicalDivisionEmailRequest";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { sendBiomedicalDivisionEmail } from "@preferredMedical/Services/CustomFormsService";
import { emailField, requiredStringField } from "@preferredMedical/Services/FormValidation";
import { generateSelectOptions } from "@preferredMedical/Services/SelectHelper";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: BiomedicalDivisionFormStyles;
    emailRecipients: string;
    widgetSettings: {
        backgroundColor: string;
        introductoryText: string;
        productNames: string;
        businessTypes: string;
    };
}

export interface BiomedicalDivisionFormStyles {
    container?: GridContainerProps;
    subContainer?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;

    introductoryText?: TypographyProps;
    errorText?: TypographyProps;

    buttonsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

export interface BiomedicalDivisionFormData {
    rent: boolean;
    purchase: boolean;
    lease: boolean;
    maintenanceRepair: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    state: string;
    pumpName: string;
    otherPumpName: string;
    businessType: string;
    companyName: string;

    phoneNumberOnChange: EventEx;
    phoneNumberUpdate: EventEx;
}

export interface BiomedicalDivisionFormErrors {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    state: string;
    pumpName: string;
    otherPumpName: string;
    businessType: string;
    companyName: string;
}

const initialFormData: BiomedicalDivisionFormData = {
    rent: false,
    purchase: false,
    lease: false,
    maintenanceRepair: false,
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    state: "",
    pumpName: "",
    otherPumpName: "",
    businessType: "",
    companyName: "",

    phoneNumberOnChange: new EventEx(),
    phoneNumberUpdate: new EventEx(),
};

const initialFormErrors: BiomedicalDivisionFormErrors = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    state: "",
    pumpName: "",
    otherPumpName: "",
    businessType: "",
    companyName: "",
};

export const biomedicalDivisionFormStyles: BiomedicalDivisionFormStyles = {
    container: {
        gap: 10,
    },
    subContainer: {
        gap: 10,
        css: css`
            margin-bottom: 15px;
            text-align: left;
            padding-left: 20px;
            padding-right: 20px;
            &:first-child {
                padding-top: 20px;
            }
        `,
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 5px;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: 50%;
                            max-width: 50%;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    introductoryText: {
        css: css`
            font-size: 1.5em;
            text-align: center;
        `,
    },
    errorText: {
        variant: "p",
        color: "danger",
        weight: 600,
        css: css`
            text-align: center;
            width: 100%;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
            margin-left: 0px;
        `,
    },
};

const getRentableProductNames = async () => {
    const rentableProductNames = await get<ProductModel[]>("api/v1/Product/RentableProducts");

    return rentableProductNames.map(x => x.productTitle);
};

const requiredFieldMessage = translate("This is a required field.");
const emailFieldMessage = translate("Enter a valid email address.");

export interface BiomedicalDivisionFormState {
    styles: BiomedicalDivisionFormStyles;
    inProgress: boolean;
    errorMessage: string;
    formData: BiomedicalDivisionFormData;
    formErrors: BiomedicalDivisionFormErrors;
    rentableProductNames: string[];
    widgetSettings: Record<string, any>;
}

class BiomedicalDivisionForm extends React.Component<OwnProps, BiomedicalDivisionFormState> {
    state = {
        styles: mergeToNew(biomedicalDivisionFormStyles, this.props.extendedStyles),
        inProgress: false,
        formData: { ...initialFormData },
        formErrors: { ...initialFormErrors },
        rentableProductNames: [...this.props.widgetSettings.productNames.split("\n")],
        widgetSettings: this.props.widgetSettings,
        errorMessage: "",
    };

    componentDidMount() {
        if (this.state.styles.container) {
            const containerCss = css`
                padding: 10px;
                background-color: ${this.props.widgetSettings.backgroundColor};
                input,
                option,
                select {
                    color: black !important;
                }
            `;

            this.setState({
                styles: { ...this.state.styles, container: { ...this.state.styles.container, css: containerCss } },
            });
        }

        getRentableProductNames().then(value => {
            this.setState({ rentableProductNames: [...value, ...this.props.widgetSettings.productNames.split("\n")] });
        });

        this.state.formData.phoneNumberOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({ formData: { ...this.state.formData, phone: phoneNumberState.value } });
            await this.setState({ formErrors: { ...this.state.formErrors, phone: phoneNumberState.error } });
        });
    }

    handleOnChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        event.persist();
        this.setState({ formData: { ...this.state.formData, [event.target.name]: event.target.value } });
    };

    checkForErrors = async () => {
        let hasError = false;
        const currentFormErrors: BiomedicalDivisionFormErrors = { ...initialFormErrors };
        const formData = this.state.formData;

        ({ hasError, errorMessage: currentFormErrors.firstName } = requiredStringField(
            formData.firstName,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.lastName } = requiredStringField(
            formData.lastName,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.email } = emailField(formData.email, emailFieldMessage, hasError));
        ({ hasError, errorMessage: currentFormErrors.state } = requiredStringField(
            formData.state,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.pumpName } = requiredStringField(
            formData.pumpName,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.businessType } = requiredStringField(
            formData.businessType,
            requiredFieldMessage,
            hasError,
        ));
        ({ hasError, errorMessage: currentFormErrors.companyName } = requiredStringField(
            formData.companyName,
            requiredFieldMessage,
            hasError,
        ));

        if (formData.pumpName === "Other") {
            ({ hasError, errorMessage: currentFormErrors.otherPumpName } = requiredStringField(
                formData.otherPumpName,
                requiredFieldMessage,
                hasError,
            ));
        }

        await this.state.formData.phoneNumberUpdate.emitAsync();
        if (this.state.formErrors.phone) {
            hasError = true;
        }

        this.setState({ ...this.state, formErrors: currentFormErrors });
        return hasError;
    };

    submitButtonClickHandler = async () => {
        const hasErrors = await this.checkForErrors();
        if (hasErrors) {
            return;
        }

        this.setState({ ...this.state, inProgress: true });

        const model: BiomedicalDivisionEmailRequest = {
            ...this.state.formData,
            emailTos: this.props.emailRecipients.split("\n"),
        };

        const response = await sendBiomedicalDivisionEmail(model).catch(() => {
            this.setState({ errorMessage: translate("Something went wrong. Please try again."), inProgress: false });
        });

        if (response) {
            window.location.href = `${window.location.href}/confirmation`;
        }
    };

    render() {
        const { styles, widgetSettings, formData, formErrors, rentableProductNames, errorMessage } = this.state;
        const productOptions = generateSelectOptions(rentableProductNames, true, "Select Pump Name");
        const businessTypesOptions = generateSelectOptions(
            this.props.widgetSettings.businessTypes.split("\n"),
            true,
            "Select Type Of Business",
        );

        return (
            <>
                <GridContainer {...styles.container}>
                    <GridContainer {...styles.subContainer}>
                        <GridItem {...styles.oneLinerGridItem}>
                            <Typography {...styles.introductoryText}>{widgetSettings.introductoryText}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.subContainer}>
                        <GridItem {...styles.twoLinerGridItem}>
                            <Checkbox
                                labelPosition="right"
                                variant="default"
                                checked={formData.rent}
                                onChange={(_e, value) => this.setState({ formData: { ...formData, rent: value } })}
                            >
                                {translate("Rent")}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <Checkbox
                                labelPosition="right"
                                variant="default"
                                checked={formData.purchase}
                                onChange={(_e, value) => this.setState({ formData: { ...formData, purchase: value } })}
                            >
                                {translate("Purchase")}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <Checkbox
                                labelPosition="right"
                                variant="default"
                                checked={formData.lease}
                                onChange={(_e, value) => this.setState({ formData: { ...formData, lease: value } })}
                            >
                                {translate("Lease")}
                            </Checkbox>
                        </GridItem>

                        <GridItem {...styles.twoLinerGridItem}>
                            <Checkbox
                                labelPosition="right"
                                variant="default"
                                checked={formData.maintenanceRepair}
                                onChange={(_e, value) =>
                                    this.setState({ formData: { ...formData, maintenanceRepair: value } })
                                }
                            >
                                {translate("Maintenance/Repair")}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                name="firstName"
                                label={translate("First Name")}
                                value={formData.firstName}
                                onChange={this.handleOnChange}
                                error={formErrors.firstName}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                name="lastName"
                                label={translate("Last Name")}
                                value={formData.lastName}
                                onChange={this.handleOnChange}
                                error={formErrors.lastName}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <PhoneNumberField
                                label={translate("Phone Number")}
                                initialValue={formData.phone}
                                onChange={formData.phoneNumberOnChange}
                                update={formData.phoneNumberUpdate}
                                data-test-selector="biomedicalDivisionPhoneNumber"
                                format="000-000-0000"
                                required={true}
                                hideRequiredAsterisk={false}
                            />
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                name="email"
                                label={translate("Email Address")}
                                value={formData.email}
                                onChange={this.handleOnChange}
                                error={formErrors.email}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                name="state"
                                label={translate("State/Province")}
                                value={formData.state}
                                onChange={this.handleOnChange}
                                error={formErrors.state}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <Select
                                name="pumpName"
                                label={translate(`Pump Name or Model`)}
                                value={formData.pumpName}
                                onChange={this.handleOnChange}
                                error={formErrors.pumpName}
                                required={true}
                            >
                                {productOptions}
                            </Select>
                        </GridItem>
                        {formData.pumpName === "Other" && (
                            <GridItem {...styles.oneLinerGridItem}>
                                <TextField
                                    name="otherPumpName"
                                    label={translate('"Other" Pump Name or Model')}
                                    value={formData.otherPumpName}
                                    onChange={this.handleOnChange}
                                    error={formErrors.otherPumpName}
                                    required={true}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.twoLinerGridItem}>
                            <Select
                                name="businessType"
                                label={translate(`Type Of Business`)}
                                value={formData.businessType}
                                onChange={this.handleOnChange}
                                error={formErrors.businessType}
                                required={true}
                            >
                                {businessTypesOptions}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.twoLinerGridItem}>
                            <TextField
                                name="companyName"
                                label={translate("Company Name")}
                                value={formData.companyName}
                                onChange={this.handleOnChange}
                                error={formErrors.companyName}
                                required={true}
                            />
                        </GridItem>
                        {errorMessage && (
                            <GridItem {...styles.oneLinerGridItem}>
                                <Typography {...styles.errorText}>{errorMessage}</Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                    <GridContainer {...styles.subContainer}>
                        <GridItem {...styles.oneLinerGridItem}>
                            <Button
                                {...styles.submitButton}
                                onClick={() => {
                                    this.submitButtonClickHandler();
                                }}
                                disabled={this.state.inProgress}
                            >
                                {translate("Submit")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridContainer>
            </>
        );
    }
}

export default BiomedicalDivisionForm;
